import { DateFormatter, parseAbsolute } from "@internationalized/date"
import { createFileRoute } from "@tanstack/react-router"
import { ReservationStatus } from "ipctg-inflow-types/InFlowEnums"
import type { ReservationMini } from "ipctg-inflow-types/InFlowReserveAPI"
import { useTranslation } from "react-i18next"

import { LinkButton } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"
import { ReservationConfirmationCard } from "@/globalComponents/molecules/productCards/ReservationCard"
import { getLocale } from "@/utilities/etc"
import {
  confirmReservePaymentQueryOptions,
  confirmReserveSetupIntentQueryOptions,
} from "@/utilities/hooks/queries/reserve"
import { receiptQueryOptions } from "@/utilities/hooks/queries/shared"
import { useTheme } from "@/utilities/hooks/useTheme"
import { normalizedAPILicensePlate } from "@/utilities/normalizers"

import styles from "./confirmation.module.css"

export const Route = createFileRoute(
  "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/confirmation"
)({
  staticData: {
    pageName: "Confirmation",
  },
  validateSearch: (search: Record<string, unknown>): { noShow: boolean } => ({
    noShow: search.noShow === true || search.noShow === "true",
  }),
  beforeLoad: async ({ context, search }) => {
    const payRsp = await context.queryClient.fetchQuery(
      search.noShow
        ? confirmReserveSetupIntentQueryOptions()
        : confirmReservePaymentQueryOptions()
    )
    const receiptRsp = await context.queryClient.fetchQuery(
      receiptQueryOptions({ receiptID: payRsp.receiptID })
    )
    return { receipt: receiptRsp.receipt }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { receipt } = Route.useRouteContext()
  const { qrLookupID } = Route.useParams()
  const { t, i18n } = useTranslation("reserve")
  const { scannedArea } = useTheme()
  const locationAddress = scannedArea!.theme.locationInformation.address
  const timezoneLocation = scannedArea!.theme.locationInformation.timeZone
  const locale = i18n.resolvedLanguage || getLocale()

  const timeFormatter = new DateFormatter(locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezoneLocation,
  })
  const formattedStartTime = timeFormatter.format(
    parseAbsolute(receipt.startTime, timezoneLocation).toDate()
  )
  const formattedEndTime = timeFormatter.format(
    parseAbsolute(receipt.endTime, timezoneLocation).toDate()
  )

  return (
    <>
      <Heading
        className="center"
        level={3}
        as={1}
        style={{ color: "var(--neutral-11)" }}
      >
        {t("confirmation.heading")}
      </Heading>
      <Card>
        <Heading level={1} as={2}>
          {t("confirmation.success")}
        </Heading>
        <ReservationConfirmationCard
          reservation={
            {
              reservationID: receipt.receiptID,
              lotName: receipt.lotName,
              bookingCalendarName: receipt.bookingCalendarName!,
              inventoryTreeRateName: receipt.rateName,
              startDateTime: receipt.startTime,
              endDateTime: receipt.endTime,
              plate: receipt.plate,
              locationName: receipt.locationName,
              locationID: "",
              marketID: "",
              enterpriseID: "",
              amountPaid: receipt.amountPaid,
              receiptIDs: [receipt.receiptID],
              status: ReservationStatus.Active,
              allowPlateChangeUntil: null,
              cancellationDeadline: null,
              locationTimezone: timezoneLocation,
              locationAddress: locationAddress,
              confirmationNumber: "",
              usageStatus: null,
              blockCancellation: false,
            } satisfies ReservationMini
          }
          variant="confirmation"
        />
        <div className={styles.detailList}>
          <div className={styles.detailRow}>
            <Text level={2} className="semi-bold">
              {t("confirmation.rate")}
            </Text>
            <Text level={2}>{receipt.rateName}</Text>
          </div>
          <div className={styles.detailRow}>
            <Text level={2} className="semi-bold">
              {t("confirmation.duration")}
            </Text>
            <Text level={2}>
              {t("cart.timeRange", {
                startTime: formattedStartTime,
                endTime: formattedEndTime,
              })}
            </Text>
          </div>
          <div className={styles.detailRow}>
            <Text level={2} className="semi-bold">
              {t("confirmation.location")}
            </Text>
            <Text level={2}>{receipt.lotName}</Text>
          </div>
          <div className={styles.detailRow}>
            <Text level={2} className="semi-bold">
              {t("confirmation.licensePlate")}
            </Text>
            <Text level={2}>{normalizedAPILicensePlate(receipt.plate)}</Text>
          </div>
        </div>
        <LinkButton
          buttonType="primary"
          to="/qr/$qrLookupID/receipt/$receiptID"
          params={{ qrLookupID, receiptID: receipt.receiptID }}
          style={{ marginTop: "var(--space-4)" }}
        >
          <Icon name="receipt" slot="left" />
          {t("viewReceipt", { ns: "receipt" })}
        </LinkButton>
      </Card>
    </>
  )
}
